import { useEffect, useRef } from 'react';
import { Mic, MicOff, Square, Sparkles, X } from 'lucide-react';

import { RealtimeVoiceSessionState, VoiceSpeedLevel } from '@/api/models';
import ErrorDetails from '@/components/Message/ErrorDetails';
import { getErrorSummary } from '@/utils/errorMessages';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Slider } from '@/components/ui/slider';
import { VoiceLogo } from './VoiceLogo';

type VoiceModePanelProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  session: RealtimeVoiceSessionState;
  onStart: () => Promise<void>;
  onStop: () => Promise<void>;
  onToggleMute: () => void;
  voiceSpeed: VoiceSpeedLevel;
  onVoiceSpeedChange: (level: VoiceSpeedLevel) => void;
};

const SPEED_LEVELS: VoiceSpeedLevel[] = ['slow', 'normal', 'fast'];
const SPEED_LABELS: Record<VoiceSpeedLevel, string> = { slow: 'Slow', normal: 'Normal', fast: 'Fast' };

const STATUS_LABELS: Record<RealtimeVoiceSessionState['status'], string> = {
  idle: 'Idle',
  connecting: 'Connecting',
  listening: 'Listening',
  speaking: 'Speaking',
  error: 'Needs attention',
  ended: 'Ended',
};

const SCROLL_STICK_THRESHOLD_PX = 24;

const VoiceModePanel = ({ open, onOpenChange, session, onStart, onStop, onToggleMute, voiceSpeed, onVoiceSpeedChange }: VoiceModePanelProps) => {
  const wasOpenRef = useRef(false);
  const transcriptRef = useRef<HTMLDivElement | null>(null);
  const stickToBottomRef = useRef(true);

  useEffect(() => {
    const justOpened = open && !wasOpenRef.current;
    wasOpenRef.current = open;

    if (!justOpened) return;
    if (session.status === 'idle' || session.status === 'ended' || session.status === 'error') {
      void onStart();
    }
  }, [open, session.status, onStart]);

  // Follow the newest text as the response streams in, unless the user has
  // scrolled up to re-read something. A new turn clears the transcript, which
  // makes it unscrollable again and re-arms the follow.
  useEffect(() => {
    const container = transcriptRef.current;
    if (!container) return;

    if (container.scrollHeight <= container.clientHeight) {
      stickToBottomRef.current = true;
      return;
    }
    if (stickToBottomRef.current) {
      container.scrollTop = container.scrollHeight;
    }
  }, [session.localTranscript, session.assistantTranscript, open]);

  const handleTranscriptScroll = () => {
    const container = transcriptRef.current;
    if (!container) return;
    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    stickToBottomRef.current = distanceFromBottom <= SCROLL_STICK_THRESHOLD_PX;
  };

  const closePanel = async () => {
    onOpenChange(false);
    await onStop();
  };

  const isSpeaking = session.status === 'speaking';
  const isListening = session.status === 'listening';
  const isConnecting = session.status === 'connecting';
  // The realtime hook publishes `listening` from the data channel's onopen
  // handler. Treat only the connected session states as animated; connecting
  // is still waiting for the transport and should remain visually still.
  const isConnected = isListening || isSpeaking;
  const isActive = isSpeaking || isListening;

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => {
      if (!nextOpen) {
        void closePanel();
        return;
      }
      onOpenChange(true);
    }}>
      <DialogContent
        className="voice-panel-max-h flex w-[calc(100%-1.5rem)] max-w-2xl flex-col gap-0 overflow-hidden border border-white/10 bg-[radial-gradient(circle_at_50%_12%,rgba(10,96,166,0.2),transparent_36%),linear-gradient(180deg,#101a1d_0%,#080c0d_100%)] p-0 text-white shadow-2xl sm:w-full sm:rounded-[32px] [&>button]:hidden"
      >
        <div className="flex shrink-0 items-center justify-between gap-3 border-b border-white/10 px-4 py-4 sm:px-8">
          <div className="min-w-0">
            <DialogTitle className="text-base font-semibold tracking-tight text-white">Voice mode</DialogTitle>
            <DialogDescription className="mt-0.5 text-xs text-white/45">
              Speak naturally and I'll respond out loud.
            </DialogDescription>
          </div>
          <button
            type="button"
            aria-label="Close voice mode"
            className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-white/55 transition hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-300"
            onClick={() => { void closePanel(); }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Scrollable middle. The header above and the controls below never
            scroll away, so the session can always be muted or ended even on a
            landscape phone where the logo does not fit. */}
        <div className="flex min-h-0 flex-auto flex-col items-center overflow-y-auto scrollbar scrollbar-gutter-stable scrollbar-thumb-accent scrollbar-track-transparent scrollbar-w-8 px-4 pt-3 sm:px-8 sm:pt-4">

          {/* --- Avatar --- */}
          {/* Caps at 228px but shrinks with the viewport height. */}
          <div className="relative flex w-[min(228px,32vh)] shrink-0 items-center justify-center">
            <VoiceLogo
              isConnected={isConnected}
              amplitude={isSpeaking ? 1 : isListening ? 0.15 : isConnecting ? 0.3 : 0}
              intensity={isConnecting ? 0.5 : 1}
              size={228}
            />
          </div>

          {/* --- Status --- */}
          <div className="mt-1 flex flex-col items-center gap-1.5" aria-live="polite">
            <div className="flex items-center gap-2.5">
              {isActive && (
                <span className={`h-2 w-2 rounded-full ${isSpeaking ? 'bg-sky-400 animate-pulse' : 'bg-emerald-400'}`} />
              )}
              {isConnecting && (
                <span className="h-2 w-2 rounded-full bg-sky-400 animate-pulse" />
              )}
              <span className={[
                'font-medium tracking-wide',
                isConnecting ? 'text-[15px] text-sky-400/80' : 'text-[13px] text-white/50',
              ].join(' ')}>
                {STATUS_LABELS[session.status]}
              </span>
            </div>
            {isConnecting && (
              <span className="text-[12px] text-white/30">Setting up your voice session…</span>
            )}
          </div>

          {/* --- Speed --- */}
          <div className="items-center w-1/2 mt-4">
            <div className="animate-in fade-in-0 slide-in-from-top-1 duration-200 ease-out" onKeyDown={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between px-1.5 pb-1">
                <span className="text-[11px] text-black/55 dark:text-white/75">Response Speed</span>
                <span className="text-[11px] text-black dark:text-white">{SPEED_LABELS[voiceSpeed]}</span>
              </div>
              <Slider
                showTicks
                className="px-1.5 pt-1 variant-small"
                min={0}
                max={2}
                step={1}
                value={[SPEED_LEVELS.indexOf(voiceSpeed)]}
                onValueChange={([index]) => onVoiceSpeedChange(SPEED_LEVELS[index])}
                aria-label="Response speed"
              />
            </div>
          </div>

          {/* --- Error --- */}
          {session.error && (
            <div role="alert" className="mt-5 w-full rounded-2xl border border-red-400/25 bg-red-400/10 px-4 py-3 text-sm text-red-200">
              <p>{getErrorSummary(session.error, session.error_details)}</p>
              <ErrorDetails details={session.error_details} error={session.error} />
            </div>
          )}

          {/* --- Tool call --- */}
          {/* Deliberately a small aside: the spoken answer is what matters, the
              tool is incidental. Full name and any error live in the tooltip. */}
          {session.activeToolCall && (
            <div
              role="status"
              title={session.activeToolCall.error || session.activeToolCall.tool_name}
              className="mt-2 flex max-w-full items-center gap-1.5 rounded-full border border-white/[0.07] bg-white/[0.03] px-2.5 py-1 text-[11px] leading-none"
            >
              <Sparkles size={11} className="shrink-0 text-sky-300/70" aria-hidden="true" />
              <span className="min-w-0 truncate text-white/55">{session.activeToolCall.tool_name}</span>
              <span className="shrink-0 text-white/30">
                {session.activeToolCall.status === 'running'
                  ? 'running'
                  : session.activeToolCall.status === 'error'
                    ? 'failed'
                    : 'done'}
              </span>
            </div>
          )}

          {/* --- Transcripts --- */}
          {/* Hidden on phones: the logo and controls are the whole interaction
              there, and the transcript costs more vertical room than it earns. */}
          <div
            ref={transcriptRef}
            onScroll={handleTranscriptScroll}
            role="log"
            aria-live="polite"
            aria-label="Live transcript"
            className="hidden max-h-36 min-h-[4.5rem] w-full flex-col gap-3 overflow-y-auto scrollbar scrollbar-gutter-stable scrollbar-thumb-accent scrollbar-track-transparent scrollbar-w-8 rounded-2xl border border-white/10 bg-[#0c1315]/70 px-4 py-4 sm:mt-7 sm:flex sm:max-h-56"
          >
            <div className="flex gap-3">
              <p className={`m-0 min-w-0 text-sm leading-relaxed ${session.localTranscript ? 'text-white/70' : 'text-white/30'}`}>
                {session.localTranscript || 'Speak to begin…'}
              </p>
            </div>
            <div className="flex gap-3 border-t border-white/[0.06] pt-3">
              <p className={`m-0 min-w-0 text-sm leading-relaxed ${session.assistantTranscript ? 'text-white/90' : 'text-white/30'}`}>
                {session.assistantTranscript || 'Your response will appear here.'}
              </p>
            </div>
          </div>
        </div>

        {/* --- Controls --- */}
        <div className="flex shrink-0 items-center justify-center gap-2 px-4 pb-6 pt-5 sm:pb-8">
            <button
              type="button"
              aria-label={session.isMuted ? 'Unmute microphone' : 'Mute microphone'}
              className={[
                'inline-flex h-11 w-11 items-center justify-center rounded-full border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-300',
                session.isMuted
                  ? 'border-red-500/30 bg-red-500/15 hover:bg-red-500/25'
                  : 'border-white/10 bg-white/[0.04] hover:bg-white/[0.08]',
              ].join(' ')}
              onClick={onToggleMute}
            >
              {session.isMuted
                ? <MicOff size={18} className="text-red-400" />
                : <Mic size={18} className="text-white/60" />}
            </button>
            <button
              type="button"
              aria-label="End voice mode"
              className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-red-500/30 bg-red-500/15 hover:bg-red-500/25 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
              onClick={() => { void closePanel(); }}
            >
              <Square size={24} strokeWidth={0} fill="#dc2626 " />
            </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default VoiceModePanel;
